import { getPhotoUploadLink } from '@/core/photoUploader/getPhotoUploadLink'
import { sendPhotoUploadLink } from '@/core/utils/sendPhotoUploadLink'
import {
  accessCodeConverter
} from '@otw/models/converters'
import { AccessCode, VehicleAccessCode } from '@otw/models/photos'
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  DocumentSnapshot,
  FirestoreDataConverter,
  getDocs,
  orderBy,
  query,
  setDoc,
  SnapshotOptions,
  updateDoc,
  where,
  WithFieldValue
} from 'firebase/firestore'
import { useCallback, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { db } from '../config/firebase'

// Photo Upload Document interface (keeping local for backward compatibility)
export interface PhotoUploadDocument {
  id: string
  userId: string
  vehicleIds: string[]
  submittedVehicles: string[]
  createdAt: string
  updatedAt: string
  status: 'active' | 'completed' | 'cancelled'
  accessCode?: string
  expiresAt?: string
  completedAt?: string
}

// Photo Upload Document Converter (keeping local for backward compatibility)
export const photoUploadDocumentConverter: FirestoreDataConverter<PhotoUploadDocument> = {
  toFirestore(photoUpload: WithFieldValue<PhotoUploadDocument>) {
    const { id, ...data } = photoUpload
    return {
      ...data,
      createdAt: data.createdAt || new Date().toISOString(),
      updatedAt: data.updatedAt || new Date().toISOString(),
      status: data.status || 'active',
      vehicleIds: data.vehicleIds || [],
      submittedVehicles: data.submittedVehicles || []
    }
  },
  fromFirestore(snapshot: DocumentSnapshot, options: SnapshotOptions): PhotoUploadDocument {
    const data = snapshot.data(options)
    
    if (!data) {
      return {
        id: snapshot.id,
        userId: '',
        vehicleIds: [],
        submittedVehicles: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: 'active'
      }
    }

    return {
      id: snapshot.id,
      userId: data.userId || '',
      vehicleIds: data.vehicleIds || [],
      submittedVehicles: data.submittedVehicles || [],
      createdAt: data.createdAt || new Date().toISOString(),
      updatedAt: data.updatedAt || new Date().toISOString(),
      status: data.status || 'active',
      accessCode: data.accessCode || undefined,
      expiresAt: data.expiresAt || undefined,
      completedAt: data.completedAt || undefined
    }
  }
}

export interface VehicleInfo {
  id: string
  vin: string
  make?: string
  model?: string
  year?: string
}

export interface CreateAccessCodeParams {
  accessCode: string
  userId?: string
  vehicles: VehicleInfo | VehicleInfo[]
  photoUploadId?: string
}

export interface SendPhotoUploadLinkParams {
  email: string
  phoneNumber?: string
  vehicleData: any
}

export interface ExistingAccessCodeResult {
  exists: boolean
  uuid?: string
  isExpired?: boolean
  expirationDate?: string
  createdAt?: string
}

export interface UsePhotoUploadAccessReturn {
  // State
  loading: boolean
  error: string | null
  
  // Access Code Operations
  createAccessCode: (params: CreateAccessCodeParams) => Promise<string>
  checkExistingAccessCode: (code: string) => Promise<ExistingAccessCodeResult>
  getVehicleAccessCodes: (vehicleIds: string[]) => Promise<VehicleAccessCode[]>
  deleteAccessCode: (accessCodeId: string) => Promise<void>
  
  // Photo Upload Operations
  createPhotoUploadDocument: (params: {
    userId: string
    vehicleIds: string[]
    expiresAt?: Date
  }) => Promise<string>
  sendPhotoUploadLink: (params: SendPhotoUploadLinkParams) => Promise<void>
  
  // Utility
  getAccessLink: (uuid: string) => string
}

export const usePhotoUploadAccess = (): UsePhotoUploadAccessReturn => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const getAccessLink = useCallback((uuid: string) => {
    let baseUrl = process.env.VITE_ENV === 'production' 
      ? 'https://uploader.optictruckworks.com/' 
      : 'https://stg.uploader.optictruckworks.com/'

    if (process.env.NODE_ENV === 'development') {
      baseUrl = 'http://localhost:3001'
    }

    return `${baseUrl}/access/${uuid}`
  }, [])

  const createAccessCode = useCallback(async (params: CreateAccessCodeParams): Promise<string> => {
    setLoading(true)
    setError(null)
    
    try {
      const { accessCode, userId, vehicles, photoUploadId } = params
      const uuid = uuidv4()
      
      // Convert single vehicle to array if necessary
      const vehicleArray = Array.isArray(vehicles) ? vehicles : [vehicles]
      
      // If no photoUploadId provided, create the photoUpload document first
      let finalPhotoUploadId = photoUploadId
      if (!finalPhotoUploadId && userId) {
        finalPhotoUploadId = await createPhotoUploadDocument({
          userId,
          vehicleIds: vehicleArray.map(v => v.id),
        })
      }
      
      const accessCodeDoc: Omit<AccessCode, 'id'> = {
        code: accessCode,
        userId,
        vehicles: vehicleArray.map(v => ({
          id: v.id,
          vin: v.vin || '',
          ...(v.make && { make: v.make }),
          ...(v.model && { model: v.model }),
          ...(v.year && { year: v.year })
        })),
        photoUploadId: finalPhotoUploadId || '',
        createdAt: new Date().toISOString(),
        expires: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 days from now
      }
      
      const docRef = doc(db, 'accessCodes', uuid).withConverter(accessCodeConverter)
      await setDoc(docRef, accessCodeDoc)
      
      // Update the photoUpload document with the accessCode UUID for reverse lookup
      if (finalPhotoUploadId) {
        const photoUploadRef = doc(db, 'photoUploads', finalPhotoUploadId).withConverter(photoUploadDocumentConverter)
        await updateDoc(photoUploadRef, {
          accessCode: uuid,
          updatedAt: new Date().toISOString()
        })
      }
      
      return uuid
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create access code'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [])

  const checkExistingAccessCode = useCallback(async (code: string): Promise<ExistingAccessCodeResult> => {
    setLoading(true)
    setError(null)
    
    try {
      // Query for access codes with the given code
      const q = query(
        collection(db, 'accessCodes').withConverter(accessCodeConverter),
        where('code', '==', code)
      )
      
      const querySnapshot = await getDocs(q)
      
      if (querySnapshot.empty) {
        return { exists: false }
      }
      
      // Get the most recent access code document
      const docs = querySnapshot.docs.sort((a, b) => {
        const aCreated = new Date(a.data().createdAt).getTime()
        const bCreated = new Date(b.data().createdAt).getTime()
        return bCreated - aCreated // Most recent first
      })
      
      const mostRecentDoc = docs[0]
      const data = mostRecentDoc.data()
      const expirationDate = data.expires
      const isExpired = new Date(expirationDate) < new Date()
      
      return {
        exists: true,
        uuid: mostRecentDoc.id,
        isExpired,
        expirationDate,
        createdAt: data.createdAt
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to check existing access code'
      setError(errorMessage)
      return { exists: false }
    } finally {
      setLoading(false)
    }
  }, [])

  const getVehicleAccessCodes = useCallback(async (vehicleIds: string[]): Promise<VehicleAccessCode[]> => {
    setLoading(true)
    setError(null)
    
    try {
      if (!vehicleIds.length) return []

      // Query for access codes ordered by expiration date
      const q = query(
        collection(db, 'accessCodes').withConverter(accessCodeConverter),
        orderBy('expires', 'asc')
      )
      
      const querySnapshot = await getDocs(q)
      const now = new Date()
      
      const accessCodes: VehicleAccessCode[] = []
      
      querySnapshot.docs.forEach(doc => {
        const data = doc.data()
        const vehicles = data.vehicles || []
        
        // Check if any of the vehicles in this access code match our target vehicle IDs
        const hasMatchingVehicle = vehicles.some(vehicle => 
          vehicleIds.includes(vehicle.id)
        )
        
        if (hasMatchingVehicle) {
          const expirationDate = new Date(data.expires)
          const isExpired = expirationDate < now
          
          accessCodes.push({
            ...data,
            isExpired
          })
        }
      })
      
      return accessCodes
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get vehicle access codes'
      setError(errorMessage)
      return []
    } finally {
      setLoading(false)
    }
  }, [])

  const deleteAccessCode = useCallback(async (accessCodeId: string): Promise<void> => {
    setLoading(true)
    setError(null)
    
    try {
      const accessCodeRef = doc(db, 'accessCodes', accessCodeId)
      await deleteDoc(accessCodeRef)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete access code'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [])

  const createPhotoUploadDocument = useCallback(async (params: {
    userId: string
    vehicleIds: string[]
    expiresAt?: Date
  }): Promise<string> => {
    setLoading(true)
    setError(null)
    
    try {
      const { userId, vehicleIds, expiresAt } = params
      
      const photoUploadData: Omit<PhotoUploadDocument, 'id'> = {
        userId,
        vehicleIds,
        submittedVehicles: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: 'active',
        ...(expiresAt && { expiresAt: expiresAt.toISOString() }),
      }
      
      const collectionRef = collection(db, 'photoUploads').withConverter(photoUploadDocumentConverter)
      const docRef = await addDoc(collectionRef, photoUploadData)
      
      return docRef.id
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create photo upload document'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [])

  const sendPhotoUploadLinkHook = useCallback(async (params: SendPhotoUploadLinkParams): Promise<void> => {
    setLoading(true)
    setError(null)
    
    try {
      const { email, phoneNumber, vehicleData } = params
      
      const link = await getPhotoUploadLink({ 
        email,
        vehicleData 
      })
      
      await sendPhotoUploadLink({ 
        link, 
        email, 
        phoneNumber: phoneNumber || '' 
      })
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send photo upload link'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    loading,
    error,
    createAccessCode,
    checkExistingAccessCode,
    getVehicleAccessCodes,
    deleteAccessCode,
    createPhotoUploadDocument,
    sendPhotoUploadLink: sendPhotoUploadLinkHook,
    getAccessLink
  }
} 